import { ArrowSmRightIcon, TrashIcon } from "@heroicons/react/outline";
import { uniqBy } from "lodash";
import moment from "moment";
import { ChangeEvent, PropsWithChildren, useState } from "react";
import {
  GoalProgressType,
  GoalScope,
  GoalState,
  GoalVisibility,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";
import { v4 as uuidv4 } from "uuid";

import ArtifactTeamsInput, {
  ArtifactTeamType,
} from "@apps/artifact/components/artifact-teams-input";
import { GoalScopeDropdownInput } from "@apps/artifact/components/goal-scope";
import { GoalVisibilityDropdownInput } from "@apps/artifact/components/goal-visibility";
import useLabel from "@apps/use-label/use-label";
import Button, { ButtonSize, buttonTheme } from "@components/button/button";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import GoalOwnerContributorDropdown from "@components/people-dropdown/goal-owner-contributor-dropdown";
import { Select, SelectOption } from "@components/select/select";
import { classNames } from "@helpers/css";

import { ArtifactCreationDialogFormType } from "./artifact-creation-dialog";

type DialogLabelProps = {
  className?: string;
};
const DialogLabel: React.FC<PropsWithChildren<DialogLabelProps>> = ({
  children,
  className = "",
}) => (
  <label
    className={classNames("text-gray-500 text-sm w-24 font-medium", className)}
  >
    {children}
  </label>
);

const getEmptyKr = () => ({
  uuid: uuidv4(),
  title: "",
  startValue: 0,
  targetValue: 100,
  assignee: undefined,
  progressType: GoalProgressType.Percentage,
});

// const noKpiOption = { value: graphqlNone, label: "None" };

const ArtifactCreationGoalFields = ({
  form,
  onChangeForm,
}: {
  form: ArtifactCreationDialogFormType;
  onChangeForm: (form: ArtifactCreationDialogFormType) => void;
}) => {
  const label = useLabel();
  const [isShowingOwnersContributors, setIsShowingOwnersContributors] =
    useState(false);

  const handleChangeDueDate = (updatedDate: Date | null) => {
    if (updatedDate === null) return;
    const dueDate = updatedDate
      ? moment(updatedDate).format("YYYY-MM-DD")
      : null;
    onChangeForm({ ...form, dueDate });
  };

  const handleChangeStartDate = (updatedDate: Date | null) => {
    if (updatedDate === null) return;
    const startDate = updatedDate
      ? moment(updatedDate).format("YYYY-MM-DD")
      : null;
    onChangeForm({ ...form, startDate });
  };

  const handleChangeGoalOwners = (owners: BasicUser[]) => {
    onChangeForm({ ...form, owners });
    if (owners.length > 0) {
      setIsShowingOwnersContributors(true);
    }
  };

  const handleChangeGoalContributors = (contributors: BasicUser[]) => {
    onChangeForm({ ...form, contributors });
  };

  const handleChangeGoalScope = ({ value: scope }: { value: GoalScope }) => {
    if (scope === GoalScope.Development) {
      onChangeForm({ ...form, scope, goalVisibility: GoalVisibility.Private });
    } else {
      onChangeForm({ ...form, scope });
    }
  };

  const handleChangeGoalVisibility = (option: SelectOption<GoalVisibility>) => {
    onChangeForm({ ...form, goalVisibility: option.value });
  };

  const handleClickAddKR = () => {
    onChangeForm({
      ...form,
      keyResults: [...form.keyResults, getEmptyKr()],
    });
  };

  const handleRemoveKr = (uuid: string) => {
    onChangeForm({
      ...form,
      keyResults: form.keyResults.filter((kr) => kr.uuid !== uuid),
    });
  };

  const handleChangeKrValue = (
    uuid: string,
    key: string,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const keyResults = form.keyResults.map((kr) => {
      if (kr.uuid === uuid) return { ...kr, [`${key}`]: e.target.value };
      return kr;
    });
    onChangeForm({ ...form, keyResults });
  };

  const handleAddTeam = (team: ArtifactTeamType) => {
    const teams = uniqBy([...form.teams, team], "id");
    onChangeForm({ ...form, teams });
  };

  const handleRemoveTeam = (id: number) => {
    const teams = form.teams.filter((team) => team.id !== id);
    onChangeForm({ ...form, teams });
  };

  const handleChangeGoalState = (option: SelectOption<GoalState>) => {
    onChangeForm({
      ...form,
      goalState: option.value,
    });
  };

  const invalidGoalDate =
    form.startDate &&
    form.dueDate &&
    moment(form.startDate).isAfter(form.dueDate);
  const involvedUserIds = [
    ...(form.owners || []),
    ...(form.contributors || []),
  ].map(({ id }) => id);

  return (
    <>
      <div className="flex items-center gap-4 py-4 px-6">
        <DialogLabel>Date</DialogLabel>
        <div className="flex-1 block">
          <div className="flex gap-2 items-center">
            <InputDatePicker
              date={form.startDate}
              onChangeDate={handleChangeStartDate}
              placeholderText="Start date"
              className="w-40"
              isClearable={false}
              maxDate={moment(form.dueDate).toDate()}
            />
            <ArrowSmRightIcon className="text-gray-500 w-4 h-4" />
            <InputDatePicker
              date={form.dueDate}
              onChangeDate={handleChangeDueDate}
              placeholderText="Due date"
              className="w-40"
              isClearable={false}
              minDate={moment(form.startDate).toDate()}
            />
          </div>
          {invalidGoalDate && (
            <div className="text-sm mt-1 text-red-700">
              The start date cannot be after the due date.
            </div>
          )}
        </div>
      </div>
      <div className="flex items-start gap-4 py-4 px-6">
        <DialogLabel className="mt-[5px]">Owners</DialogLabel>
        <div className="flex-1 flex flex-col gap-2 text-sm">
          <div className="flex-1 flex gap-4 items-center">
            <div className="flex flex-col gap-2">
              <GoalOwnerContributorDropdown
                isSettingMultipleUsers={isShowingOwnersContributors}
                users={form.owners || []}
                excludeUserIds={involvedUserIds}
                onChangeUsers={handleChangeGoalOwners}
                placeholder="Add an owner"
                mustHaveOneUserMinimum
              />
            </div>
            {!isShowingOwnersContributors && (
              <Button
                text="Add more owners & contributors"
                theme={buttonTheme.text}
                size={ButtonSize.small}
                onClick={() => setIsShowingOwnersContributors(true)}
                className="text-gray-400"
              />
            )}
          </div>
        </div>
      </div>
      {isShowingOwnersContributors && (
        <div className="flex items-start gap-4 py-4 px-6">
          <DialogLabel className="mt-[5px]">Contributors</DialogLabel>
          <div className="flex-1 flex flex-col gap-2 text-sm">
            <div className="flex-1 flex gap-4 items-center">
              <div className="flex flex-col gap-2">
                <GoalOwnerContributorDropdown
                  isSettingMultipleUsers
                  users={form.contributors || []}
                  excludeUserIds={involvedUserIds}
                  onChangeUsers={handleChangeGoalContributors}
                  placeholder="Add a contributor"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-4 py-4 px-6">
        <DialogLabel>Type</DialogLabel>
        <div className="flex-1 w-full flex flex-col gap-2 -ml-3">
          <div className="flex">
            <GoalScopeDropdownInput
              artifact={{
                ...form,
                scope: form.scope || GoalScope.Personal,
                state: GoalState.Open,
                canUpdate: { permission: true },
              }}
              onChangeScope={handleChangeGoalScope}
            />
          </div>
          {form.scope === GoalScope.Team && (
            <div className="pl-2.5 ml-px">
              <ArtifactTeamsInput
                teams={form.teams}
                onAddTeam={handleAddTeam}
                onRemoveTeam={handleRemoveTeam}
                canEdit
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 py-4 px-6 text-sm">
        <DialogLabel>State</DialogLabel>
        <div className="flex-1 w-full flex flex-col gap-2 -ml-2">
          <div className="flex items-center gap-2">
            <Select<GoalState>
              options={[
                {
                  label: "Open",
                  value: GoalState.Open,
                },
                {
                  label: "Draft",
                  value: GoalState.Draft,
                },
              ]}
              buttonProps={{
                theme: buttonTheme.text,
              }}
              value={
                form.goalState === GoalState.Draft
                  ? GoalState.Draft
                  : GoalState.Open
              }
              onChange={handleChangeGoalState}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 py-4 px-6">
        <DialogLabel>Visibility</DialogLabel>
        <div className="flex-1 flex -ml-2">
          <GoalVisibilityDropdownInput
            artifact={{
              ...form,
              canUpdate: { permission: true },
              goalVisibility: form.goalVisibility || GoalVisibility.Public,
            }}
            onChangeVisibility={handleChangeGoalVisibility}
          />
        </div>
      </div>
      <div className="flex items-start gap-4 py-4 px-6">
        <DialogLabel className="mt-0.5">
          {label("key result", { pluralize: true, capitalize: true })}
        </DialogLabel>
        <div className="flex-1 flex flex-col">
          {form.keyResults.map((keyResult, i) => (
            <div
              key={keyResult.uuid}
              className={classNames(
                "flex gap-4 border-b border-gray-100 pb-2",
                i > 0 && "pt-2"
              )}
            >
              <div className="flex-1 flex flex-col gap-3">
                <div className="flex-1 flex gap-1.5 items-center max-w-92">
                  <Input
                    type="text"
                    className="flex-1 min-w-0"
                    aria-label="Key result title input"
                    value={keyResult.title}
                    placeholder={`Type ${label("key result")} title...`}
                    onChange={(e) =>
                      handleChangeKrValue(keyResult.uuid, "title", e)
                    }
                  />
                  <Button
                    theme={buttonTheme.iconGray}
                    icon={TrashIcon}
                    onClick={() => handleRemoveKr(keyResult.uuid)}
                  />
                </div>
              </div>
            </div>
          ))}
          <div className={classNames(form.keyResults.length > 0 && "pt-1.5")}>
            <Button
              text={`Add ${label("key result")}`}
              rounded
              size={ButtonSize.medium}
              onClick={handleClickAddKR}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ArtifactCreationGoalFields;
